import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PreferencesTree } from '@/types/preferences';

interface PreferencesState {
  preferences: PreferencesTree;
  hydratedAt: number | null;
  setAll: (tree: PreferencesTree) => void;
  setPath: (path: string, value: unknown) => void;
  forgetPath: (path: string) => void;
}

const CHANNEL_NAME = 'preferences-sync';
const channel: BroadcastChannel | null =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

function setAtPath(tree: PreferencesTree, path: string, value: unknown): PreferencesTree {
  const segments = path.split('.');
  const next: Record<string, unknown> = structuredClone(tree) as Record<string, unknown>;
  let cursor: Record<string, unknown> = next;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    if (typeof cursor[key] !== 'object' || cursor[key] === null) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
  return next as PreferencesTree;
}

function forgetAtPath(tree: PreferencesTree, path: string): PreferencesTree {
  const segments = path.split('.');
  const next: Record<string, unknown> = structuredClone(tree) as Record<string, unknown>;
  let cursor: Record<string, unknown> | undefined = next;
  for (let i = 0; i < segments.length - 1; i++) {
    const v = cursor?.[segments[i]];
    if (typeof v !== 'object' || v === null) return tree;
    cursor = v as Record<string, unknown>;
  }
  if (cursor) delete cursor[segments[segments.length - 1]];
  return next as PreferencesTree;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      preferences: {},
      hydratedAt: null,
      setAll: (tree) => {
        set({ preferences: tree, hydratedAt: Date.now() });
        channel?.postMessage({ type: 'setAll', tree });
      },
      setPath: (path, value) => {
        set((state) => ({ preferences: setAtPath(state.preferences, path, value) }));
      },
      forgetPath: (path) => {
        set((state) => ({ preferences: forgetAtPath(state.preferences, path) }));
      },
    }),
    {
      name: 'preferences-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ preferences: state.preferences }),
    }
  )
);

// Receive cross-tab updates.
channel?.addEventListener('message', (event) => {
  const msg = event.data as { type: string; tree?: PreferencesTree };
  if (msg.type === 'setAll' && msg.tree) {
    usePreferencesStore.setState({ preferences: msg.tree });
  }
});

/** Test-only: reset the in-memory store. Does NOT touch broadcast channel. */
export function __resetPreferencesStoreForTests(): void {
  usePreferencesStore.setState({ preferences: {}, hydratedAt: null });
}
